import React, { useEffect, useState, useMemo } from "react";
import { Table, Typography, Space, Input, message, Button, Modal, Tag, Tooltip } from "antd";
import AxiosOICStat from "AxiosOICStat";
import { EditOutlined, DeleteOutlined, PlusOutlined, CheckCircleOutlined, SyncOutlined, CloseCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import ImfIndicatorMappingModal from 'components/modals/ImfIndicatorMappingModal';

const { Text } = Typography;
const { Search } = Input;

export default function ImfIndicatorMappingTab({ refreshTrigger }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  const [checkingAll, setCheckingAll] = useState(false);
  const [query, setQuery] = useState("");
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  useEffect(() => { fetchMappings(); }, [refreshTrigger]);

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const res = await AxiosOICStat.get("/external_api/imf/indicator-mappings");
      setData(res.data?.mappings || res.data || []);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to load IMF indicator mappings.";
      message.error(detail);
    } finally { setLoading(false); }
  };

  const handleCheck = (id) => async () => {
    setCheckingId(id);
    try {
      const res = await AxiosOICStat.post(`/external_api/imf/check-indicator/${id}`);
      const newStatus = res.data?.status;
      setData(prev => prev.map(d => d.id === id ? { ...d, status: newStatus, last_check_time: res.data?.last_check_time } : d));
      message.success(newStatus ? 'API data available' : 'No data from API');
    } catch (err) {
      message.error(err?.response?.data?.detail || 'Check failed');
    } finally { setCheckingId(null); }
  };

  const handleCheckAll = async () => {
    setCheckingAll(true);
    try {
      const res = await AxiosOICStat.post("/external_api/imf/check-all-indicators");
      const results = res.data?.results || [];
      setData(prev => prev.map(d => {
        const found = results.find(r => r.id === d.id);
        return found ? { ...d, status: found.status, last_check_time: new Date().toISOString() } : d;
      }));
      const ok = results.filter(r => r.status === true).length;
      const fail = results.filter(r => r.status === false).length;
      message.success(`Checked all: ${ok} OK, ${fail} no data`);
    } catch (err) {
      message.error(err?.response?.data?.detail || 'Check all failed');
    } finally { setCheckingAll(false); }
  };

  const handleDelete = (id) => () => {
    Modal.confirm({ title: 'Delete Mapping', content: 'Delete this mapping?', onOk: async () => {
      try {
        await AxiosOICStat.delete(`/external_api/imf/indicator-mappings/${id}`);
        setData(prev => prev.filter(d => d.id !== id));
        message.success('Deleted');
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || 'Delete failed';
        message.error(detail);
      }
    }});
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return data;
    const q = query.toLowerCase();
    return data.filter(r => {
      const oicCode = String(r.oic_ind_code || '');
      const oicName = String(r.oic_ind_name || '').toLowerCase();
      const imfCode = String(r.imf_indicator_code || '').toLowerCase();
      const dataset = String(r.dataset || '').toLowerCase();
      return oicCode.includes(q) || oicName.includes(q) || imfCode.includes(q) || dataset.includes(q);
    });
  }, [data, query]);

  const columns = [
    {
      title: 'OIC Indicator Code',
      dataIndex: 'oic_ind_code',
      key: 'oic_ind_code',
      width: 100,
      sorter: (a, b) => {
        const aVal = a.oic_ind_code;
        const bVal = b.oic_ind_code;
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        const aIsNum = !Number.isNaN(aNum);
        const bIsNum = !Number.isNaN(bNum);
        if (aIsNum && bIsNum) return aNum - bNum;
        if (aIsNum) return -1;
        if (bIsNum) return 1;
        return String(aVal || '').localeCompare(String(bVal || ''));
      },
      render: (v) => <Text strong>{v}</Text>
    },
    {
      title: 'OIC Indicator Name',
      dataIndex: 'oic_ind_name',
      key: 'oic_ind_name',
      width: 220,
      sorter: (a, b) => (String(a.oic_ind_name || '')).localeCompare(String(b.oic_ind_name || '')),
      render: (v) => v ? <Text>{v}</Text> : <Text type="secondary">—</Text>
    },
    {
      title: 'IMF Indicator Code',
      dataIndex: 'imf_indicator_code',
      key: 'imf_indicator_code',
      width: 220,
      sorter: (a, b) => (String(a.imf_indicator_code || '')).localeCompare(String(b.imf_indicator_code || '')),
      render: (v) => v ? <Text code>{v}</Text> : <Text type="secondary">—</Text>
    },
    {
      title: 'Dataset',
      dataIndex: 'dataset',
      key: 'dataset',
      width: 100,
      render: (v) => v ? <Tag color="blue">{v}</Tag> : <Text type="secondary">—</Text>
    },
    {
      title: 'IMF Indicator Label',
      dataIndex: 'imf_indicator_label',
      key: 'imf_indicator_label',
      width: 300,
      sorter: (a, b) => (String(a.imf_indicator_label || '')).localeCompare(String(b.imf_indicator_label || '')),
      render: (v) => v ? <Text>{v}</Text> : <Text type="secondary">—</Text>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 220,
      sorter: (a, b) => {
        const sa = a.status === true ? 1 : a.status === false ? -1 : 0;
        const sb = b.status === true ? 1 : b.status === false ? -1 : 0;
        return sb - sa;
      },
      render: (v, record) => {
        const timeStr = record.last_check_time ? new Date(record.last_check_time).toLocaleString() : null;
        if (v === true) return <Space><Tag icon={<CheckCircleOutlined />} color="success">Available</Tag><Text type="secondary" style={{ fontSize: 12 }}>{timeStr}</Text></Space>;
        if (v === false) return <Space><Tag icon={<CloseCircleOutlined />} color="error">Unavailable</Tag><Text type="secondary" style={{ fontSize: 12 }}>{timeStr}</Text></Space>;
        return <Tag icon={<QuestionCircleOutlined />} color="default">Unknown</Tag>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Tooltip title="Check API for data availability">
            <Button
              icon={checkingId === record.id ? <SyncOutlined spin /> : <CheckCircleOutlined />}
              size="small"
              onClick={handleCheck(record.id)}
              loading={checkingId === record.id}
            />
          </Tooltip>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            style={{ width: 32 }}
            onClick={() => { setEditingRecord(record); setAddModalVisible(true); }}
          />
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            size="small"
            style={{ width: 32 }}
            onClick={handleDelete(record.id)}
          />
        </Space>
      )
    }
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space>
        <Search allowClear placeholder="Search by indicator / IMF code / dataset…" onSearch={setQuery} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 420 }} />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setAddModalVisible(true); setEditingRecord(null); }}>Add New Indicator Mapping</Button>
        <Button icon={checkingAll ? <SyncOutlined spin /> : <CheckCircleOutlined />} onClick={handleCheckAll} loading={checkingAll}>Check All</Button>
      </Space>

      <Table rowKey="id" loading={loading} columns={columns} dataSource={filtered} pagination={{ pageSize: 20, showSizeChanger: true }} bordered />

      <ImfIndicatorMappingModal
        visible={addModalVisible}
        record={editingRecord}
        onCancel={() => { setAddModalVisible(false); setEditingRecord(null); }}
        onSuccess={() => { setAddModalVisible(false); setEditingRecord(null); fetchMappings(); }}
      />
    </Space>
  );
}
